import { motion } from 'motion/react';
import type { ChatMessage } from './types';
import { linkifyText } from './linkify';
import { CTAButton } from './CTAButton';
import { formatAssistantMeta, formatUserMeta } from './messageMeta';

type Props = {
  message: ChatMessage;
};

/**
 * Soft bubbles: assistant on white-in-tint; user elevated on white with ring (card-style chat).
 */
export function MessageBubble({ message }: Props) {
  const isUser = message.role === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 480, damping: 32 }}
      className={`flex w-full flex-col gap-1.5 ${isUser ? 'items-end' : 'items-start'}`}
    >
      <div
        className={
          isUser
            ? 'max-w-[90%] rounded-[18px] rounded-br-md border border-neutral-200/80 bg-white px-3 py-2.5 text-[14px] leading-relaxed text-neutral-900 shadow-sm sm:rounded-[20px] sm:px-3.5 sm:py-3 sm:text-[15px]'
            : `max-w-[94%] rounded-[18px] rounded-bl-md border px-3 py-2.5 text-[14px] leading-relaxed sm:rounded-[20px] sm:px-3.5 sm:py-3 sm:text-[15px] ${
                message.isError
                  ? 'border-amber-200/90 bg-amber-50/90 text-amber-950'
                  : 'border-transparent bg-white/80 text-neutral-900 shadow-sm'
              }`
        }
      >
        <div className="whitespace-pre-wrap break-words">{linkifyText(message.content)}</div>
        {!isUser && message.cta ? <CTAButton cta={message.cta} /> : null}
      </div>
      <p className={`max-w-[94%] px-0.5 text-[10px] font-medium text-neutral-400 sm:px-1 sm:text-[11px] ${isUser ? 'text-right' : 'text-left'}`}>
        {isUser ? formatUserMeta(message.createdAt) : formatAssistantMeta(message.createdAt)}
      </p>
    </motion.div>
  );
}
